export interface CCResult {
  numComponents: number;
  componentSizes: number[];
  largestComponentLabel: number;
  labelMap: Int32Array;
}

export interface OrientedBBox {
  cx: number;
  cy: number;
  width: number;
  height: number;
  angleRad: number;
}

export type BinaryMask = Uint8Array | Uint8ClampedArray;

const isFg = (v: number) => v > 0;

function findRoot(parent: Int32Array, x: number): number {
  let root = x;
  while (parent[root] !== root) {
    root = parent[root];
  }
  while (parent[x] !== root) {
    const next = parent[x];
    parent[x] = root;
    x = next;
  }
  return root;
}

function unionLabels(parent: Int32Array, a: number, b: number): number {
  const ra = findRoot(parent, a);
  const rb = findRoot(parent, b);
  if (ra === rb) {
    return ra;
  }
  const min = ra < rb ? ra : rb;
  const max = ra < rb ? rb : ra;
  parent[max] = min;
  return min;
}

export function findConnectedComponents(
  mask: BinaryMask,
  width: number,
  height: number,
  connectivity: 4 | 8 = 8,
): CCResult {
  const labels = new Int32Array(width * height);
  const parent = new Int32Array(width * height + 1);
  let nextLabel = 1;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      if (!isFg(mask[idx])) {
        continue;
      }

      let best = 0;
      const tryNeighbor = (nx: number, ny: number) => {
        if (nx < 0 || ny < 0 || nx >= width || ny >= height) {
          return;
        }
        const nLabel = labels[ny * width + nx];
        if (nLabel === 0) {
          return;
        }
        best = best === 0 ? nLabel : unionLabels(parent, best, nLabel);
      };

      tryNeighbor(x - 1, y);
      tryNeighbor(x, y - 1);
      if (connectivity === 8) {
        tryNeighbor(x - 1, y - 1);
        tryNeighbor(x + 1, y - 1);
      }

      if (best === 0) {
        parent[nextLabel] = nextLabel;
        labels[idx] = nextLabel;
        nextLabel++;
      } else {
        labels[idx] = best;
      }
    }
  }

  const remap = new Int32Array(nextLabel);
  let compCount = 0;
  const sizes: number[] = [];

  for (let i = 0; i < width * height; i++) {
    const l = labels[i];
    if (l === 0) {
      continue;
    }
    const root = findRoot(parent, l);
    let mapped = remap[root];
    if (mapped === 0) {
      compCount++;
      mapped = compCount;
      remap[root] = mapped;
      sizes.push(0);
    }
    labels[i] = mapped;
    sizes[mapped - 1]++;
  }

  let largestComponentLabel = 0;
  let largestSize = 0;
  for (let i = 0; i < sizes.length; i++) {
    if (sizes[i] > largestSize) {
      largestSize = sizes[i];
      largestComponentLabel = i + 1;
    }
  }

  return {
    numComponents: compCount,
    componentSizes: sizes,
    largestComponentLabel,
    labelMap: labels,
  };
}

export function keepLargestComponent(
  mask: BinaryMask,
  width: number,
  height: number,
): void {
  const cc = findConnectedComponents(mask, width, height, 8);
  if (cc.numComponents <= 1) {
    return;
  }
  const keep = cc.largestComponentLabel;
  const labels = cc.labelMap;
  for (let i = 0; i < mask.length; i++) {
    if (labels[i] !== keep) {
      mask[i] = 0;
    }
  }
}

export function fillHoles(
  mask: BinaryMask,
  width: number,
  height: number,
  maxHoleSize?: number,
): void {
  const inverted = new Uint8Array(mask.length);
  for (let i = 0; i < mask.length; i++) {
    inverted[i] = mask[i] > 0 ? 0 : 1;
  }

  const cc = findConnectedComponents(inverted, width, height, 4);
  const touchesBorder = new Uint8Array(cc.numComponents + 1);
  const labels = cc.labelMap;

  const checkBorder = (x: number, y: number) => {
    const l = labels[y * width + x];
    if (l > 0) {
      touchesBorder[l] = 1;
    }
  };
  for (let x = 0; x < width; x++) {
    checkBorder(x, 0);
    checkBorder(x, height - 1);
  }
  for (let y = 0; y < height; y++) {
    checkBorder(0, y);
    checkBorder(width - 1, y);
  }

  for (let i = 0; i < mask.length; i++) {
    const l = labels[i];
    if (l === 0 || touchesBorder[l]) {
      continue;
    }
    if (maxHoleSize !== undefined && cc.componentSizes[l - 1] > maxHoleSize) {
      continue;
    }
    mask[i] = 255;
  }
}

export function rasterizeOBB(
  obb: OrientedBBox,
  mask: BinaryMask,
  width: number,
  height: number,
  value: number = 255,
): void {
  const { cx, cy, width: bw, height: bh, angleRad } = obb;
  const halfW = bw / 2;
  const halfH = bh / 2;
  const cosA = Math.cos(-angleRad);
  const sinA = Math.sin(-angleRad);

  const corners = [
    [-halfW, -halfH],
    [halfW, -halfH],
    [halfW, halfH],
    [-halfW, halfH],
  ].map(([lx, ly]) => {
    const wx = cx + lx * Math.cos(angleRad) - ly * Math.sin(angleRad);
    const wy = cy + lx * Math.sin(angleRad) + ly * Math.cos(angleRad);
    return [wx, wy];
  });

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const [x, y] of corners) {
    if (x < minX) {
      minX = x;
    }
    if (x > maxX) {
      maxX = x;
    }
    if (y < minY) {
      minY = y;
    }
    if (y > maxY) {
      maxY = y;
    }
  }
  minX = Math.max(0, Math.floor(minX));
  maxX = Math.min(width - 1, Math.ceil(maxX));
  minY = Math.max(0, Math.floor(minY));
  maxY = Math.min(height - 1, Math.ceil(maxY));

  for (let y = minY; y <= maxY; y++) {
    const dy = y - cy;
    for (let x = minX; x <= maxX; x++) {
      const dx = x - cx;
      const lx = dx * cosA - dy * sinA;
      const ly = dx * sinA + dy * cosA;
      if (Math.abs(lx) <= halfW && Math.abs(ly) <= halfH) {
        mask[y * width + x] = value;
      }
    }
  }
}

/**
 * Scanline-fill a polygon into the mask. A pixel is set when its CENTER
 * (x + 0.5, y + 0.5) lies inside the polygon. For the pixel-edge polygons
 * maskToCocoPolygons emits (integer lattice coordinates) this inverts the
 * tracing exactly — save → load reproduces the identical mask, and the
 * half-integer sample lines can never hit a vertex, so there is no
 * vertex-on-scanline ambiguity to special-case.
 */
export function rasterizePolygon(
  vertices: ReadonlyArray<readonly [number, number]>,
  mask: BinaryMask,
  width: number,
  height: number,
  value: number = 255,
): void {
  if (vertices.length < 3) {
    return;
  }
  let minY = Infinity;
  let maxY = -Infinity;
  for (const [, y] of vertices) {
    if (y < minY) {
      minY = y;
    }
    if (y > maxY) {
      maxY = y;
    }
  }
  // Rows whose center line y + 0.5 crosses the polygon's y-range.
  const yStart = Math.max(0, Math.ceil(minY - 0.5));
  const yEnd = Math.min(height - 1, Math.ceil(maxY - 0.5) - 1);

  for (let y = yStart; y <= yEnd; y++) {
    const sampleY = y + 0.5;
    const xs: number[] = [];
    for (let i = 0; i < vertices.length; i++) {
      const [x1, y1] = vertices[i];
      const [x2, y2] = vertices[(i + 1) % vertices.length];
      // Half-open interval [y_lo, y_hi) keeps the crossing parity correct
      // for float polygons whose vertex happens to sit on a sample line.
      const yLo = Math.min(y1, y2);
      const yHi = Math.max(y1, y2);
      if (sampleY < yLo || sampleY >= yHi) {
        continue;
      }
      const t = (sampleY - y1) / (y2 - y1);
      xs.push(x1 + t * (x2 - x1));
    }
    xs.sort((a, b) => a - b);
    for (let k = 0; k + 1 < xs.length; k += 2) {
      // Pixel centers strictly between the crossing pair.
      const xLo = Math.max(0, Math.ceil(xs[k] - 0.5));
      const xHi = Math.min(width - 1, Math.ceil(xs[k + 1] - 0.5) - 1);
      const row = y * width;
      for (let x = xLo; x <= xHi; x++) {
        mask[row + x] = value;
      }
    }
  }
}
